import React, { useMemo } from 'react';
import { BuildingIcon, ChevronDownIcon } from 'lucide-react';
import { sampleOrgUnits, flattenOrgUnits, type OrgUnit } from '../../financial/sharedFinancialData';
interface OrgUnitSelectorProps {
  value?: string; // OrgUnit ID
  onChange: (orgUnitId: string, orgUnit: OrgUnit | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  filterByType?: string; // Optional: filter by OrgUnit type (e.g., 'ENTITY', 'SITE', 'DEPT')
  showPath?: boolean; // Show full path in dropdown
}
export function OrgUnitSelector({
  value,
  onChange,
  label = 'Organisation Unit',
  placeholder = 'Select organisation unit',
  required = false,
  error,
  filterByType,
  showPath = true
}: OrgUnitSelectorProps) {
  // Flatten OrgUnits for selection
  const flatOrgUnits = useMemo(() => {
    const flattened = flattenOrgUnits(sampleOrgUnits);
    if (filterByType) {
      return flattened.filter(ou => ou.orgUnitType?.code === filterByType);
    }
    return flattened;
  }, [filterByType]);
  const selectedOrgUnit = flatOrgUnits.find(ou => ou.id === value);
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const orgUnitId = e.target.value;
    const orgUnit = flatOrgUnits.find(ou => ou.id === orgUnitId) || null;
    onChange(orgUnitId, orgUnit);
  };
  return <div data-id="element-20228">
      {label && <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-20229">
          {label} {required && <span className="text-red-500" data-id="element-20230">*</span>}
        </label>}
      <div className="relative" data-id="element-20231">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-20232">
          <BuildingIcon className="h-4 w-4 text-gray-400" data-id="element-20233" />
        </div>
        <select value={value || ''} onChange={handleChange} required={required} className={`block w-full pl-10 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md ${error ? 'border-red-300' : ''}`} data-id="element-20234">
          <option value="" data-id="element-20235">{placeholder}</option>
          {flatOrgUnits.map(orgUnit => <option key={orgUnit.id} value={orgUnit.id} data-id="element-20236">
              {orgUnit.name}
              {showPath && ` (${orgUnit.path})`}
              {orgUnit.orgUnitType && ` - ${orgUnit.orgUnitType.name}`}
            </option>)}
        </select>
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-20237">
          <ChevronDownIcon className="h-4 w-4 text-gray-400" data-id="element-20238" />
        </div>
      </div>
      {error && <p className="mt-1 text-sm text-red-600" data-id="element-20239">{error}</p>}
      {selectedOrgUnit && <p className="mt-1 text-xs text-gray-500" data-id="element-20240">
          Selected: {selectedOrgUnit.path}
          {selectedOrgUnit.glCodeSegment && ` • GL Segment: ${selectedOrgUnit.glCodeSegment}`}
        </p>}
    </div>;
}